import { Link } from "react-router-dom";

/*
 * Blog article: "How TRT Plus Analyzes Your Bloodwork"
 * Refined version — v2.
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                TRT Plus splits bloodwork analysis into two jobs. First, a
                deterministic engine does the reasoning: it scores every
                marker against personalized, TRT-calibrated ranges, computes
                a weighted Biomarker Score, and runs your cardiovascular
                and metabolic markers through a cascade that sets your
                individual hematocrit threshold and testosterone ceiling.
                Then AI takes that finished, structured
                result and turns it into your 8-section Bloodwork Report in
                plain English. The math is deterministic and repeatable. The
                AI writes it up. Same labs in, same analysis out.
            </p>

            <h2>Two jobs, kept separate</h2>
            <p>
                Analyzing your bloodwork is really two jobs, not one.
            </p>
            <p>
                The first is reasoning: what does this number mean, against
                the right range, in the context of every other marker, and
                where does it sit relative to a ceiling you're managing to?
                That work needs to be deterministic, testable, and bounded.
                The same inputs should always produce the same result.
                That's traditional engineering.
            </p>
            <p>
                The second is interpretation: taking that result and
                explaining it in language you can actually act on. That work
                needs to be fluent, adaptive, and readable. That's what
                modern AI is genuinely great at.
            </p>
            <p>
                TRT Plus keeps these separate. The engine decides. The AI
                explains.
            </p>

            <h2>What the engine does before the AI sees anything</h2>
            <p>
                This is the part that matters most. The engine processes
                your labs first, deterministically and the same way every
                time, and only its structured output is handed off. Every
                score, range, and ratio is produced by a programmatically
                engineered model, not approximated by a language model. And
                the models and ranges themselves come from deep analysis of
                de-identified, real-world TRT clinic data. That's the
                foundation under every threshold, score, and ceiling the
                engine applies. That pre-processing is the product:
            </p>
            <ul>
                <li>
                    <strong>Personalized reference ranges.</strong> Every
                    marker is scored against TRT-calibrated ranges. In-range
                    for the general population, much of which is prediabetic,
                    overweight, and inflamed, is the wrong target for a man
                    on TRT who trains and eats with intent. Your ranges
                    should reflect the room at Dick's Sporting Goods, not
                    the checkout line at Walmart.
                </li>
                <li>
                    <strong>A weighted Biomarker Score.</strong> Markers are
                    weighted by clinical importance and read as
                    relationships and ratios (testosterone to estradiol,
                    testosterone to SHBG), not isolated values. Marker and
                    ratio scores are composited into one score you can
                    track over time.
                </li>
                <li>
                    <strong>The Cardio-Metabolic Cascade.</strong> Six
                    cardiovascular and metabolic markers — systolic and
                    diastolic blood pressure, ApoB, CRP, body fat, and
                    HbA1c — feed into your hematocrit threshold, which in
                    turn sets your personalized testosterone ceiling. Lean
                    and metabolically healthy extends a higher ceiling;
                    rising risk tightens it.
                </li>
                <li>
                    <strong>The Growth Axis.</strong> Your IGF-1 range
                    scales the same way, with your glycemic control:
                    estradiol enables the conversion of growth hormone into
                    IGF-1, and HbA1c gates how high it runs.
                </li>
                <li>
                    <strong>Androgenic Load (A:E).</strong> Your
                    androgen-to-estrogen picture accounts for DHT's
                    outsized receptor potency and non-linear amplification,
                    not a flat testosterone-to-estradiol number.
                </li>
                <li>
                    <strong>Zakharov Free T.</strong> Free testosterone is
                    solved with a model that holds up at TRT and
                    supraphysiological levels, where the standard population
                    formula drifts.
                </li>
            </ul>
            <p>
                By the time the AI is involved, the judgment is already
                made. Every number has been scored, every range
                personalized, every ceiling set, deterministically and
                identically on every run.
            </p>

            <h2>The cascade in action: hematocrit</h2>
            <p>
                Hematocrit is the clearest example of why cascading matters,
                because a single number means very different things
                depending on what surrounds it.
            </p>
            <p>
                Your hematocrit isn't read alone. It's gated by the
                cardio-metabolic cascade. If your blood pressure, ApoB, CRP,
                body fat, and HbA1c are all in range, the engine recognizes
                a lower-risk profile and applies your context-adjusted
                threshold. As each of those markers slips into amber and
                then red, the ceiling tightens progressively. Every
                downgrade in the cascade pulls your hematocrit threshold
                down another step. Your acceptable HCT isn't a fixed line.
                It's context-adjusted, and it moves with your cardiovascular
                picture.
            </p>
            <p>
                Paste your bloodwork with a hematocrit of 54 into ChatGPT
                and it may tell you to donate blood immediately. Run the
                same result through TRT Plus, and the engine can see that
                your blood pressure is perfect, your atherogenic particles
                are in range, your inflammation is low, you're lean, and
                your glucose control is excellent. A hematocrit of 54 here
                doesn't cause alarm, it is read as tolerated and monitored
                in context. See the difference?
            </p>

            <h2>Then the AI: your 8-section report</h2>
            <p>
                Now the AI goes to work on the engine's output, not your
                raw labs. It takes the scored markers, the personalized
                ranges, the ratios, and the ceilings the engine produced and
                writes your{" "}
                <Link to="/#bloodwork">8-section AI Bloodwork Report</Link>.
                It opens with your recommendations: the top 3–5
                highest-priority actions, with each recommendation
                spelling out the
                lifestyle, supplement, and medicinal strategies to get it
                done. From there it moves into the deeper sections: your
                Anabolic Potential Rating, Hormonal Balance, Responder Type,
                a Cardiovascular Risk Profile, and a Healthspan Assessment.
            </p>
            <p>
                The AI's job here is interpretation and language: narrating
                the engine's math in a way you can read and act on.
            </p>

            <figure className="my-12 flex flex-col items-center">
                <div className="relative w-full max-w-[300px]">
                    <div className="phone-frame">
                        <div className="phone-screen">
                            <img
                                src="/blog/ai-report-8-sections.webp"
                                alt="TRT Plus 8-section AI Biomarker Analysis report on iPhone — Overall Score, Recommendations, Protocol and Dose Response, Hormonal Balance, Muscle-Building Potential, Compound Analysis, Growth Hormone, Healthspan Assessment, and Cardiovascular Risk Profile expanded to show the Clogging Quartet."
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover object-top"
                            />
                        </div>
                    </div>
                </div>
                <figcaption className="mt-6 text-sm text-white/50 italic text-center">
                    The 8-section AI Biomarker Analysis, with Cardiovascular Risk Profile expanded.
                </figcaption>
            </figure>

            <h2>Why this produces consistent, personal results</h2>
            <p>
                Because the reasoning lives in the engine, the same labs
                always produce the same analysis. When a marker moves from
                one draw to the next, you know it's your blood changing,
                not the model's mood. And because your ranges and ceilings
                are set to you rather than a population average, the read
                is personal.
            </p>
            <p>
                A deterministic foundation, an AI that speaks plain English.
            </p>

            <h2>Frequently asked questions</h2>

            <h3>How does TRT Plus use AI?</h3>
            <p>
                Only for interpretation and language. AI takes the engine's
                finished, structured result and writes it up as your report.
                The scoring, the personalized ranges, and the clinical
                logic are the engine's job, and that part is deterministic.
            </p>

            <h3>How is this different from asking a chatbot to read my labs?</h3>
            <p>
                A general model gives you a fresh interpretation each time
                and leans on population reference ranges by default. TRT
                Plus fixes the ranges and the scoring in the engine first,
                so the analysis is personalized and repeatable, then uses
                AI to write it up. The difference isn't the AI; it's the
                engineered model in front of it.
            </p>

            <h3>Can I see how the engine works?</h3>
            <p>
                Yes. The schematic, the formulas, and the cited studies
                behind each model are laid out in the app and on the engine
                page. <Link to="/engine">See the engine</Link>.
            </p>

            <h3>Does this replace my healthcare provider?</h3>
            <p>
                No. It gives you a consistent, contextualized read so the
                conversation with your healthcare provider starts from
                solid ground. It doesn't make the decision for you.
            </p>

            <h3>Do other TRT apps do this?</h3>
            <p>
                Hell no. Most apps advertising "AI bloodwork analysis" are
                wrapping the same general model you already have access to.
                You could dump your labs into Grok, type "analyze this,"
                and get the same kind of answer, with the same
                inconsistency. The engine is the hard part, and it's the
                part that's missing. A deterministic framework built on
                real-world TRT clinic data is what actually turns your labs
                into a personalized, repeatable read.
            </p>

            <h3>Can I continue the discussion with my own AI?</h3>
            <p>
                Yes. A hand-off button packages your full report, your lab
                markers, and the framework and model context behind them,
                the cascade and growth axis included, and shares it to your
                installed Claude, ChatGPT, or Grok so you can keep
                exploring. This is the opposite of dumping raw labs into a
                chatbot: you're handing the general model the engine's
                finished, structured analysis along with the framework it
                was built on, so the conversation stays anchored to a
                deterministic read rather than the model improvising from
                scratch.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does not
                constitute medical advice. Consult with your healthcare
                provider before making medical decisions.
            </p>

            <h3>See the engine for yourself</h3>
            <p>
                Every score, range, and ceiling in TRT Plus comes from a
                deterministic model. You can read the actual schematic and
                formulas. <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/engine">
                        The engine schematic and formulas
                    </Link>
                </li>
                <li>
                    <Link to="/#bloodwork">
                        Your 8-section AI Bloodwork Report
                    </Link>
                </li>
                <li>
                    <Link to="/#testimator">
                        The Testimator: modeling your individual response
                    </Link>
                </li>
                <li>
                    <Link to="/blog/the-growth-axis">
                        The Growth Axis: Why Your IGF-1 Range Scales With
                        Your Health
                    </Link>
                </li>
            </ul>
        </>
    );
}
